import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';

export class CustomValidators {
  /**
   * Valida que un rango final sea mayor que un rango inicial y viceversa
   * @param startControlName Nombre del control de inicio
   * @param endControlName Nombre del control de fin
   * @returns ValidatorFn
   */
  static rangeSequence({ startControlName, endControlName }: { startControlName: string; endControlName: string }): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }

      const startValue = group.get(startControlName)?.value;
      const endValue = group.get(endControlName)?.value;
      if (startValue == null || endValue == null) {
        return null;
      }

      const start: number = Number(startValue);
      const end: number = Number(endValue);
      if (end <= start) {
        return { rangeSequence: true };
      }

      return null;
    };
  }

  /**
   * Valida que un rango no se cruze con rangos existentes
   * @param args argumentos
   * @Note Los fieldName del obj se pasan solo si se llaman diferente a como en el form.
   * @returns ValidatorFn
   */
  static rangeOverlap<T>(args: RangeOverlapArgs<T>): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }

      const startValue = group.get(args.startControlName)?.value;
      const endValue = group.get(args.endControlName)?.value;
      if (startValue == null || endValue == null) {
        return null;
      }

      const startField: keyof T = args.startFieldName ?? args.startControlName as keyof T;
      const endField: keyof T = args.endFieldName ?? args.endControlName as keyof T;
      const idField: keyof T = args.idFieldName ?? args.idControlName as keyof T;

      const start: number = Number(startValue);
      const end: number = Number(endValue);
      const currentId = group.get(args.idControlName)?.value;
      const otherRanges: T[] = args.ranges.filter(e => e[idField] !== currentId);

      const hasOverlap: boolean = otherRanges.some(e => {
        const existingStart: number = Number(e[startField]);
        const existingEnd: number = Number(e[endField]);

        return (
          (start > existingStart && start < existingEnd) ||
          (end > existingStart && end < existingEnd) ||
          (start < existingStart && end > existingEnd) ||
          (start === existingStart && end === existingEnd)
        );
      });

      if (hasOverlap) {
        return { rangeOverlap: true };
      }

      return null;
    };
  }

  /**
   * Valida que la combinación de un conjunto de campos no exista ya entre una lista de registros existentes
   * @param fieldNames nombres de los campos cuya combinación debe ser única
   * @param existing callback que retorna la lista actual de registros existentes contra la cual comparar
   * @returns ValidatorFn
   */
  static uniqueCombination<T extends Record<string, any>>(fieldNames: string[], existing: () => T[]): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const values = fieldNames.map(fieldName => group.value[fieldName]);
      if (values.some(value => !value)) {
        return null;
      }

      const isDuplicate: boolean = existing().some(record => fieldNames.every((fieldName, index) => record[fieldName] === values[index]));
      return isDuplicate ? { uniqueCombination: true } : null;
    };
  }
}

export interface RangeOverlapArgs<T extends Record<string, any>> {
  startControlName: string;
  endControlName: string;
  idControlName: string;
  ranges: T[];
  startFieldName?: keyof T;
  endFieldName?: keyof T;
  idFieldName?: keyof T;
}
